import { isEmpty, uniq } from "lodash";

import { actionTypes as appStateActionTypes } from "../../../appState/actions";
import appStateSelectors from "../../../appState/selectors";
import { characterWidthMapActionTypes, characterWidthMapSelectors } from "../../../characterWidthMap";
import { definitionActionTypes } from "../../../definition";
import { colorActionTypes, colorSelectors } from "../../../definition/colors";
import { typeSelectors } from "../../../definition/types";
import { preferencesActionTypes } from "../../../preferences";
import rootActionTypes from "../../../rootActionTypes";
import displayCacheSelectors from "../../selectors";
import { getAffectedActionIds } from "./getIdsFromActions";

// Possible change paths:
//  -- User changes linked item shortLabel, initials, label or color
//  -- User adds or removes a dependency to this item
//  -- Global color property is changed
//  -- Data is first loaded

const areRelevantItemValuesSame = (newItem, prevItem) =>
  newItem &&
  prevItem &&
  newItem.color === prevItem.color &&
  newItem.shortLabel === prevItem.shortLabel &&
  newItem.initials === prevItem.initials &&
  newItem.type === prevItem.type &&
  newItem.label === prevItem.label;

const areItemDependenciesTheSame = (newItem, prevItem) =>
  newItem &&
  prevItem &&
  newItem.blockingDependencies === prevItem.blockingDependencies &&
  newItem.blockedByDependencies === prevItem.blockedByDependencies;

const globalActionTypes = [
  //#!if readonly !== true
// colorActionTypes.CHANGE_COLOR,
// colorActionTypes.DELETE_COLOR,
  //#!endif
  definitionActionTypes.REPLACE_DEFINITIONS,
  rootActionTypes.LOAD_FILE,
  rootActionTypes.UPDATE_FILE,
  rootActionTypes.LOAD_SAMPLE,
  appStateActionTypes.SET_FONT_PERCENTAGE,
  preferencesActionTypes.SET_GLOBAL_FONT_SIZE,
  characterWidthMapActionTypes.ADD_CHARACTER_WIDTHS
];

const getBlockIdentifiers = (
  item,
  isSubject,
  itemsById,
  dependenciesById,
  typesById,
  colorsById,
  fontSize,
  widthMap
) => {
  const fieldName = isSubject ? "blockingDependencies" : "blockedByDependencies";
  return item.get(fieldName).map(depId => {
    const dep = dependenciesById.get(depId);
    const depItemId = isSubject ? dep.object : dep.subject;
    return displayCacheSelectors.getIdentifierDisplayForItem(
      itemsById.get(depItemId),
      typesById,
      colorsById,
      fontSize,
      widthMap
    );
  });
};

const updateBlockIdentifiers = (
  state,
  action,
  newAndChangedIdSet,
  newItemsById,
  prevItemsById,
  newDependenciesById,
  prevDependenciesById,
  newGlobalState
) => {
  let { affectedIds, matchedGlobal } = getAffectedActionIds(state, action, globalActionTypes, []);

  if (!matchedGlobal) {
    // We only need to consider newAndChangedIdSet + any items that are related to those that changed
    const dataChangedIds = newAndChangedIdSet.filter(
      cId => !areRelevantItemValuesSame(newItemsById.get(cId), prevItemsById.get(cId))
    );

    const dependencyChangedIds = newAndChangedIdSet.filter(
      cId => !areItemDependenciesTheSame(newItemsById.get(cId), prevItemsById.get(cId))
    );

    // We need to look at items related to those items
    const linkedIds = dataChangedIds.reduce((result, cId) => {
      const currItem = newItemsById.get(cId);
      const prevItem = prevItemsById.get(cId);
      const currBlockingIds = currItem.blockingDependencies.map(depId => newDependenciesById.get(depId).object);
      const currBlockedByIds = currItem.blockedByDependencies.map(depId => newDependenciesById.get(depId).subject);
      result.push(...currBlockingIds.toArray());
      result.push(...currBlockedByIds.toArray());

      if (prevItem) {
        const prevBlockingIds = prevItem.blockingDependencies.map(depId => prevDependenciesById.get(depId).object);
        const prevBlockedByIds = prevItem.blockedByDependencies.map(depId => prevDependenciesById.get(depId).subject);
        result.push(...prevBlockingIds.toArray());
        result.push(...prevBlockedByIds.toArray());
      }
      return result;
    }, []);

    affectedIds = uniq([...dependencyChangedIds.toArray(), ...linkedIds]);
  }

  if (affectedIds.length === 0) {
    return state;
  }

  const typesById = typeSelectors.getTypesById(newGlobalState);
  const colorsById = colorSelectors.getColorsById(newGlobalState);
  const fontSize = appStateSelectors.getFontSize(newGlobalState);
  const widthMap = characterWidthMapSelectors.getCharacterWidthMap(newGlobalState);

  return state.withMutations(s => {
    affectedIds.forEach(cId => {
      const item = newItemsById.get(cId);
      const rec = state.get(cId);

      const blockingIdentifiers = getBlockIdentifiers(
        item,
        true,
        newItemsById,
        newDependenciesById,
        typesById,
        colorsById,
        fontSize,
        widthMap
      );
      const blockedByIdentifiers = getBlockIdentifiers(
        item,
        false,
        newItemsById,
        newDependenciesById,
        typesById,
        colorsById,
        fontSize,
        widthMap
      );

      let changes = {};
      const oldBlockingString = JSON.stringify(rec.blockingIdentifiers.toJS());
      const newBlockingString = JSON.stringify(blockingIdentifiers.toJS());
      if (oldBlockingString !== newBlockingString) {
        changes.blockingIdentifiers = blockingIdentifiers;
      }

      const oldBlockedByString = JSON.stringify(rec.blockedByIdentifiers.toJS());
      const newBlockedByString = JSON.stringify(blockedByIdentifiers.toJS());
      if (oldBlockedByString !== newBlockedByString) {
        changes.blockedByIdentifiers = blockedByIdentifiers;
      }

      if (!isEmpty(changes)) {
        s.update(cId, d => d.merge(changes));
      }
    });
  });
};

export default updateBlockIdentifiers;
